<script lang="ts">
	import { format } from 'svelte-i18n';

	import type { Device } from 'src/types/Device';

	import { devices, devices_status, home_data, rooms } from 'src/stores';

	import {
		Alert,
		Collapse,
		LabelV2,
		SelectCard,
		SelectCardV2,
		Stack,
		Text,
	} from '@Shelly/shelly-design-system';

	import { getDeviceImage } from 'src/helpers/device_state_view_helpers';
	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import {
		getDeviceDescription,
		groupEnergyMetersByRoom,
		isDeviceOnline,
		MAIN_ENERGY_METER_TYPES,
	} from './main-energy-meter-helpers';
	import {
		getActiveSolarSetupType,
		getConfiguredMeterIdsByRole,
		type PvConfigurations,
	} from './solar-setup-helpers';

	export let selectedDeviceId: string | null = null;
	export let idPrefix = 'home-main-em';
	export let legacy = false;

	let roomExpanded: Record<string, boolean> = {};
	let roomsSeeded = false;

	$: selectCard = legacy ? SelectCard : SelectCardV2;
	$: resolveRoomName = (roomId: number | undefined): string =>
		$rooms?.[roomId]?.name || $format('text_no_room_label', { default: 'Other' });
	$: roomGroups = groupEnergyMetersByRoom(
		Object.values($devices) as Device[],
		MAIN_ENERGY_METER_TYPES,
		$devices_status,
		resolveRoomName,
		idPrefix,
	);
	$: solarGridDevices = getPVSetupDevices(getActiveSolarSetupType($home_data)).gridDevices;
	$: solarGridHintName = solarGridDevices[0]?.name || solarGridDevices[0]?.type;
	$: solarGridMeterIds = getConfiguredMeterIdsByRole(
		$home_data?.pv_configurations as PvConfigurations | undefined,
		'grid',
	);
	$: if (!roomsSeeded && roomGroups.length > 0) {
		roomExpanded = Object.fromEntries(roomGroups.map((roomGroup) => [roomGroup.id, true]));
		roomsSeeded = true;
	}

	const toggleRoom = (roomId: string): void => {
		roomExpanded = { ...roomExpanded, [roomId]: !roomExpanded[roomId] };
	};

	const handleSelectDevice = (device: Device): void => {
		selectedDeviceId = device.id;
	};
</script>

<Text class="mb-m">
	{$format('description_select_main_energy_meter', {
		default: 'Select the device that measures the total consumption of this home.',
	})}
</Text>

{#if solarGridHintName}
	<Alert
		class="mb-m"
		heading={$format('solar_setup_main_meter_hint_title', {
			default: 'Suggested from Solar setup',
		})}
		message={$format('solar_setup_main_meter_hint', {
			default:
				'{name} is already designated as a solar grid meter. That is usually the main energy meter for this home.',
			values: { name: solarGridHintName },
		})}
	/>
{/if}

{#if roomGroups.length === 0}
	<Text class="text-color-muted mb-m">
		{$format('description_no_energy_meters', { default: 'No compatible energy meters found.' })}
	</Text>
{:else}
	<Stack gap="s" class="mb-m">
		{#each roomGroups as roomGroup (roomGroup.id)}
			<Collapse
				id={roomGroup.id}
				title={roomGroup.roomName}
				expanded={!!roomExpanded[roomGroup.id]}
				on:click={() => toggleRoom(roomGroup.id)}
			>
				<div class="device-grid">
					{#each roomGroup.devices as device (device.id)}
						{@const online = isDeviceOnline(device, $devices_status)}
						<svelte:component
							this={selectCard}
							type="horizontal"
							variant="radio"
							class="width-full"
							title={device.name || device.type}
							descriptiveText={getDeviceDescription(device.type, online, $format)}
							image={getDeviceImage(device) ?? ''}
							icon="fas fa-bolt"
							backgroundColor="color-base"
							selected={selectedDeviceId === device.id}
							disabled={!online}
							on:click={() => handleSelectDevice(device)}
						>
							{#if solarGridMeterIds.includes(device.id)}
								<LabelV2
									slot="rightBadge"
									size="s"
									value={$format('label_already_solar_meter', {
										default: 'Already a solar meter',
									})}
								/>
							{/if}
						</svelte:component>
					{/each}
				</div>
			</Collapse>
		{/each}
	</Stack>
{/if}

<style>
	.device-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: var(--spacing-xs);
	}

	.device-grid > :global(.select-card-v2-container),
	.device-grid > :global(.select-card-container) {
		min-width: 0;
	}

	@media (max-width: 639px) {
		.device-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
